import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import {
  complianceApi,
  type CompliancePreviewRequest,
  type FilingRequirement,
  type ReportDiff,
} from '../lib/api';

const DEFAULT: CompliancePreviewRequest = {
  entity: 'llc',
  states: ['California'],
  foreignStates: [],
  revenue: '100kTo1m',
  assets: 'under250k',
  hasEmployees: ['none'],
  sellsGoodsOrServices: 'yes',
  hasPhysicalPresence: 'no',
  hiresContractors: 'no',
};

function PayloadEditor({
  value,
  onChange,
}: {
  value: CompliancePreviewRequest;
  onChange: (v: CompliancePreviewRequest) => void;
}) {
  const [text, setText] = useState(JSON.stringify(value, null, 2));
  const [parseError, setParseError] = useState('');

  return (
    <>
      <Box
        component="textarea"
        value={text}
        onChange={(e) => {
          const v = e.target.value;
          setText(v);
          try {
            onChange(JSON.parse(v));
            setParseError('');
          } catch (err) {
            setParseError((err as Error).message);
          }
        }}
        sx={{
          width: '100%',
          minHeight: 320,
          p: 2,
          bgcolor: '#0f172a',
          color: '#e2e8f0',
          borderRadius: 1,
          fontFamily: 'monospace',
          fontSize: 12.5,
          border: 'none',
          resize: 'vertical',
        }}
      />
      {parseError && (
        <Typography variant="caption" color="error" mt={1}>
          {parseError}
        </Typography>
      )}
    </>
  );
}

function FilingRow({
  filing,
  variant,
}: {
  filing: FilingRequirement;
  variant: 'added' | 'removed';
}) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        borderLeft: 4,
        borderLeftColor: variant === 'added' ? 'success.main' : 'error.main',
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} mb={0.5}>
        {variant === 'added' ? (
          <AddIcon fontSize="small" color="success" />
        ) : (
          <RemoveIcon fontSize="small" color="error" />
        )}
        <Typography variant="subtitle2" fontWeight={700}>
          {filing.title}
        </Typography>
        {filing.state && <Chip size="small" label={filing.state} />}
        <Chip size="small" variant="outlined" label={filing.priority} />
      </Stack>
      <Typography variant="body2" color="text.secondary">
        {filing.description}
      </Typography>
    </Paper>
  );
}

export default function ComplianceDiff() {
  const [before, setBefore] = useState<CompliancePreviewRequest>(DEFAULT);
  const [after, setAfter] = useState<CompliancePreviewRequest>({
    ...DEFAULT,
    foreignStates: ['New York'],
    hasPhysicalPresence: 'yes',
  });
  const [diff, setDiff] = useState<ReportDiff | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const run = async () => {
    setLoading(true);
    setError('');
    try {
      const result = await complianceApi.diff(before, after);
      setDiff(result);
    } catch (e) {
      const err = e as { response?: { data?: { error?: string } }; message?: string };
      setError(err.response?.data?.error ?? err.message ?? 'Diff failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Box mb={3}>
        <Typography variant="h5" fontWeight={700}>
          Compliance Diff
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Compare two assessment payloads to see what filings are added or removed by a change.
        </Typography>
      </Box>

      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" fontWeight={700} mb={1}>
            Before
          </Typography>
          <PayloadEditor value={before} onChange={setBefore} />
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" fontWeight={700} mb={1}>
            After
          </Typography>
          <PayloadEditor value={after} onChange={setAfter} />
        </Grid>
      </Grid>

      <Stack direction="row" spacing={2} mb={3}>
        <Button
          variant="contained"
          startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <CompareArrowsIcon />}
          onClick={run}
          disabled={loading}
        >
          {loading ? 'Diffing…' : 'Run diff'}
        </Button>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {diff && (
        <>
          <Stack direction="row" spacing={2} mb={3}>
            <Chip
              icon={<AddIcon />}
              color="success"
              label={`${diff.added.length} added`}
              variant={diff.added.length ? 'filled' : 'outlined'}
            />
            <Chip
              icon={<RemoveIcon />}
              color="error"
              label={`${diff.removed.length} removed`}
              variant={diff.removed.length ? 'filled' : 'outlined'}
            />
            <Chip label={`${diff.unchanged} unchanged`} variant="outlined" />
            <Chip
              label={`${diff.countsBefore.total} → ${diff.countsAfter.total} total`}
              variant="outlined"
            />
          </Stack>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" fontWeight={700} mb={1}>
                Added ({diff.added.length})
              </Typography>
              <Stack spacing={1}>
                {diff.added.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No new filings introduced.
                  </Typography>
                ) : (
                  diff.added.map((f, i) => <FilingRow key={i} filing={f} variant="added" />)
                )}
              </Stack>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" fontWeight={700} mb={1}>
                Removed ({diff.removed.length})
              </Typography>
              <Stack spacing={1}>
                {diff.removed.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No filings dropped.
                  </Typography>
                ) : (
                  diff.removed.map((f, i) => <FilingRow key={i} filing={f} variant="removed" />)
                )}
              </Stack>
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  );
}
